import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import request, { TOKEN_KEY } from "../../utils/request";
import logoImage from "../../assets/opsmate/logo.png";
import { storeCurrentUser } from "../../utils/user";

function extractApiErrorMessage(err: unknown): string | undefined {
  const data = (err as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (!data || typeof data !== "object") return undefined;
  const { Message, Form } = data as { Message?: unknown; Form?: unknown };
  if (Array.isArray(Message) && typeof Message[0] === "string" && Message[0]) {
    return Message[0];
  }
  if (Form && typeof Form === "object") {
    const first = Object.values(Form as Record<string, unknown>).find(
      (v): v is string => typeof v === "string" && !!v,
    );
    if (first) return first;
  }
  return undefined;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === "zh";
  const [searchParams, setSearchParams] = useSearchParams();
  // 仅当 URL 中带 pwdLogin=1 时显示用户名密码登录表单，否则只显示 OAuth 登录
  const showPwdLogin = searchParams.get("pwdLogin") === "1";

  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [error, setError] = useState("");

  const completeSsoLogin = useCallback(async () => {
    setLoading(true);
    setSsoLoading(true);
    try {
      const userRes = await request.get("/_account/checkuserinfo", {
        params: { IsApi: true },
      });
      storeCurrentUser(userRes.data);
      navigate("/", { replace: true });
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setError(t("login.ssoFailed"));
    } finally {
      setLoading(false);
      setSsoLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    const ssoToken = searchParams.get("sso_token");
    const ssoError = searchParams.get("sso_error");

    if (ssoError) {
      const detail = searchParams.get("sso_error_detail");
      // 后端只回传稳定错误码，这里映射到对应多语言文案；未知码回退到通用提示
      const knownCodes = [
        "provider_error",
        "missing_params",
        "state_expired",
        "no_id_token",
        "nonce_mismatch",
        "no_oid",
        "no_permission",
        "account_disabled",
        "login_failed",
      ];
      const base = knownCodes.includes(ssoError)
        ? t(`login.ssoError.${ssoError}`)
        : t("login.ssoFailed");
      setError(detail ? `${base}（${detail}）` : base);
      setSearchParams({}, { replace: true });
      return;
    }

    if (ssoToken) {
      localStorage.setItem(TOKEN_KEY, ssoToken);
      setSearchParams({}, { replace: true });
      completeSsoLogin();
    }
  }, [completeSsoLogin, searchParams, setSearchParams, t]);

  const toggleLanguage = () => {
    i18n.changeLanguage(isZh ? "en" : "zh");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!account || !password) {
      setError(t("login.errorRequired"));
      return;
    }

    setLoading(true);
    try {
      const loginRes = await request.post("/_account/loginjwt", {
        Account: account,
        Password: password,
      });

      const token = loginRes.data?.access_token;
      if (!token) {
        setError(t("login.errorFailed"));
        setLoading(false);
        return;
      }

      localStorage.setItem(TOKEN_KEY, token);

      const userRes = await request.get("/_account/checkuserinfo", {
        params: { IsApi: true },
      });
      storeCurrentUser(userRes.data);

      navigate("/", { replace: true });
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err) || t("login.errorFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSsoLogin = async () => {
    setSsoLoading(true);
    setError("");
    try {
      const res = await request.get("/_sso/authorize");
      const authorizeUrl = res.data?.authorizeUrl;
      if (authorizeUrl) {
        window.location.href = authorizeUrl;
      } else {
        setError(t("login.ssoFailed"));
        setSsoLoading(false);
      }
    } catch {
      setError(t("login.ssoFailed"));
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#F8F8FA" }}>
      {/* 顶部品牌栏 */}
      <header className="flex-shrink-0" style={{ backgroundColor: "#830051" }}>
        <div className="w-48 flex items-center justify-between px-2">
          <div className="flex items-center">
            <img src={logoImage} alt="AstraZeneca" className="h-full object-contain" />
          </div>
        </div>
      </header>

      {/* 登录主体 */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* 登录卡片 */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8">
            {/* 标题区域 */}
            <div className="text-center mb-8">
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
                style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
              >
                <i className="ri-shield-user-line text-2xl" style={{ color: "#830051" }}></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                {t("login.title")}
              </h1>
              <p className="text-sm text-gray-500 mt-2">{t("login.subtitle")}</p>
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-500 text-base"></i>
                <span className="text-sm text-red-600">{error}</span>
              </div>
            )}

            {/* SSO 登录按钮 */}
            <button
              type="button"
              onClick={handleSsoLogin}
              disabled={ssoLoading || loading}
              className="w-full py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {ssoLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  {t("login.ssoLoading")}
                </>
              ) : (
                <>
                  <i className="ri-microsoft-line text-base" style={{ color: "#830051" }}></i>
                  {t("login.ssoButton")}
                </>
              )}
            </button>

            {showPwdLogin && (
              <>
                {/* 分隔线 */}
                <div className="flex items-center my-6">
                  <div className="flex-1 border-t border-gray-200" />
                  <span className="px-4 text-sm text-gray-400">{t("login.orDivider")}</span>
                  <div className="flex-1 border-t border-gray-200" />
                </div>

                {/* 登录表单 */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* 用户名 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      {t("login.username")}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-user-line text-base"></i>
                      </div>
                      <input
                        type="text"
                        value={account}
                        onChange={(e) => setAccount(e.target.value)}
                        placeholder={t("login.usernamePlaceholder")}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#830051] focus:border-transparent transition-colors"
                      />
                    </div>
                  </div>

                  {/* 密码 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      {t("login.password")}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-lock-line text-base"></i>
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t("login.passwordPlaceholder")}
                        className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#830051] focus:border-transparent transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                      >
                        <i
                          className={
                            showPassword ? "ri-eye-off-line text-base" : "ri-eye-line text-base"
                          }
                        ></i>
                      </button>
                    </div>
                  </div>

                  {/* 登录按钮 */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 text-white rounded-lg text-sm font-medium hover:bg-[#6a0042] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ backgroundColor: "#830051" }}
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base"></i>
                        {t("login.loggingIn")}
                      </>
                    ) : (
                      <>
                        <i className="ri-login-box-line text-base"></i>
                        {t("login.submit")}
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* 底部版权 */}
          <p className="text-center text-xs text-gray-400 mt-6">
            &copy; {new Date().getFullYear()} AstraZeneca. {t("login.copyright")}
          </p>
        </div>
      </div>
    </div>
  );
}
